import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { NAV, SITE } from "@/lib/site";
import { EASE } from "@/lib/motion";

const MobileMenu = ({ onClose }: { onClose: () => void }) => {
  useEffect(() => {
    const lenis = (window as unknown as { __lenis?: { stop: () => void; start: () => void } }).__lenis;
    lenis?.stop();
    document.documentElement.style.overflow = "hidden";
    return () => {
      lenis?.start();
      document.documentElement.style.overflow = "";
    };
  }, []);

  return (
    <motion.div
      data-testid="mobile-menu"
      className="fixed inset-0 z-[110] flex flex-col bg-bg lg:hidden"
      initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
      animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
      exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
      transition={{ duration: 0.6, ease: EASE }}
    >
      <div className="flex h-16 items-center justify-between px-5 sm:h-20 sm:px-10">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mute">Menu</span>
        <button
          type="button"
          data-testid="mobile-menu-close-button"
          aria-label="Fermer le menu"
          onClick={onClose}
          className="font-mono text-[11px] uppercase tracking-[0.25em] text-mute"
        >
          Fermer ✕
        </button>
      </div>

      <nav className="flex flex-1 flex-col justify-center px-5 sm:px-10" aria-label="Menu mobile">
        {NAV.map((item, i) => (
          <span key={item.path} className="block overflow-hidden border-b border-line">
            <motion.div
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.15 + i * 0.07 }}
            >
              <Link
                to={item.path}
                onClick={onClose}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="group flex items-baseline gap-4 py-3 text-ink"
              >
                <span className="font-mono text-xs text-mute">{item.n}</span>
                <span className="text-[11vw] font-bold uppercase leading-none tracking-tightest sm:text-6xl">
                  {item.label}
                </span>
              </Link>
            </motion.div>
          </span>
        ))}
      </nav>

      <motion.div
        className="flex items-center justify-between px-5 pb-10 sm:px-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.6 }}
      >
        <div className="flex flex-col gap-1">
          <a
            href={`mailto:${SITE.email}`}
            data-testid="mobile-menu-email"
            className="text-sm text-mute"
          >
            {SITE.email}
          </a>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
            {SITE.location}
          </span>
        </div>
        <Link
          to="/contact"
          onClick={onClose}
          data-testid="mobile-menu-cta"
          className="inline-flex items-center gap-2 border border-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink"
        >
          Un projet ? <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </motion.div>
    </motion.div>
  );
};

export default MobileMenu;
